import { createCategories, updateCategories } from "@/graphql/mutations";
import { uploadFileToBucket } from "@/utils/helper/aws-bucketUpload";
import { generateClient } from "@/utils/helper/aws-generateClient-singleton";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { LoaderCircle, ImageIcon, X } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useAppDispatch } from "@/store/hooks";
import { addCategory } from "@/store/slices/appSlice";
import { createCategorySchema, editCategorySchema } from "@/schemas/categorySchema";
import capitalizeWords from "@/utils/helper/capitalizeWords";

interface CategoryFormProps {
  category?: any;
  isEditing?: boolean;
}

const CategoryForm = ({ category, isEditing = false }: CategoryFormProps) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isExistingImage, setIsExistingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch();
  const client = generateClient();

  const schema = isEditing ? editCategorySchema : createCategorySchema;

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: capitalizeWords(category?.name || ""),
      description: category?.description || "",
      image: category?.image || undefined,
    },
  });

  useEffect(() => {
 
    if (isEditing && category?.image && typeof category.image === "string") {

      setImagePreview(category.image);
      setIsExistingImage(true);
    }
  }, [category, isEditing]);

  const handleImageChange = (files: FileList | null, onChange: (file: File) => void) => {
    if (files && files[0]) {
      const selectedImage = files[0];
      onChange(selectedImage);
      setIsExistingImage(false);

      const previewUrl = URL.createObjectURL(selectedImage);
      setImagePreview(previewUrl);
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview) {
      if (!isExistingImage) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(null);
      setIsExistingImage(false);
      form.setValue("image", "");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const onSubmit = async (data: any) => {
    try {
      form.setValue("name", data.name.trim());
      if (data.description) {
        form.setValue("description", data.description.trim());
      }

      let imageUrl = data.image;

      if (data.image instanceof File) {
        imageUrl = await uploadFileToBucket(data.image, "category-logos");
      } else if (!data.image) {
        imageUrl = "";
      }

      const categoryData = {
        id: isEditing ? category.id : uuidv4(),
        name: data.name.toLowerCase(),
        description: data.description || "",
        image: imageUrl,
        is_active: isEditing ? category.is_active : true,
        is_deleted: isEditing ? category.is_deleted : false,
        updated_at: new Date().toISOString(),
        ...(isEditing ? {} : { created_at: new Date().toISOString() }),
      };

      const mutation = isEditing ? updateCategories : createCategories;
      const successMessage = isEditing
        ? "Category updated successfully!"
        : "Category added successfully!";

      const newCategory = await executeGraphQLMutation({
        query: mutation,
        variables: {
          input: categoryData,
        },
        client,
      });

      if (newCategory) {
        dispatch(addCategory(newCategory));
        toast.success(successMessage);
        if (!isEditing) {
          form.reset();
          setImagePreview(null);
        }
      }
    } catch (error) {
      console.error(`Error during category ${isEditing ? "update" : "addition"}:`, error);
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const isSubmitting = form.formState.isSubmitting;
  const submitButtonText = isEditing ? "Update Category" : "Add Category";
  const loadingText = isEditing ? "Updating Category..." : "Adding Category...";

  return (
    <Card className="max-w-6xl w-[52rem]  ">
      {/* <CardHeader>
        <CardTitle className="text-2xl">{title}</CardTitle>
      </CardHeader> */}
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Form fields remain the same */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter category name" {...field} disabled={isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter category description (optional)"
                      className="min-h-[100px] resize-y"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="image"
              render={({ field: { onChange, value, ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>Category Image</FormLabel>
                  <FormControl>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isSubmitting}
                        >
                          Choose File
                        </Button>
                        <span className="text-sm text-gray-500">
                          {imagePreview
                            ? `Image selected${isExistingImage ? " (existing)" : ""}`
                            : "No image selected"}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mb-2">
                        Allowed file types: jpeg, jpg, png
                      </div>
                      <Input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        ref={fileInputRef}
                        onChange={(e) => handleImageChange(e.target.files, onChange)}
                        disabled={isSubmitting}
                        className="hidden"
                      />
                      {imagePreview ? (
                        <div className="mt-2 relative">
                          <div className="border border-gray-200 rounded-md p-2 w-fit relative">
                            <img
                              src={imagePreview}
                              alt="Category preview"
                              className="max-w-[200px] max-h-[200px] object-contain"
                            />
                            <X
                              onClick={handleRemoveImage}
                              className="absolute -top-2 -right-2 z-10 w-5 h-5 cursor-pointer bg-white rounded-full p-1 text-black border border-gray-300"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-full max-w-[200px] h-[100px] border border-dashed border-gray-300 rounded-md bg-gray-50">
                          <ImageIcon className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={isSubmitting} className="w-full hover:bg-black/90">
              {isSubmitting ? (
                <>
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  {loadingText}
                </>
              ) : (
                submitButtonText
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default CategoryForm;
